import { useEffect, useMemo, useRef, useState } from 'react';
import { ClassNames } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import {
  AlertCircle, ArrowLeft, ArrowRightLeft, CheckCircle2, CircleGauge, Disc3,
  ExternalLink, MapPin, PlusCircle, RefreshCw, Search, Settings2, Truck, Warehouse, Wrench,
} from 'lucide-react';
import PropTypes from 'prop-types';
import ROUTE from 'src/utils/routeConstants';
import { USER_ROLES } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { tyreManagementApi, unwrapList } from '../api/tyreManagementApi';
import useAsyncData from '../hooks/useAsyncData';
import { tyreQueryKeys } from '../api/queryKeys';
import useTyreMutation from '../hooks/useTyreMutation';
import EmptyState from '../components/EmptyState';
import ClaudeVehicleLayout from '../components/ClaudeVehicleLayout';
import ClaudePositionMap from '../components/ClaudePositionMap';
import StateBadge from '../components/StateBadge';
import FormField from '../components/FormField';
import SelectControl from '../components/SelectControl';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { formatDate, formatNumber } from '../lib/utils';
import { resolveSelectedVehicleId } from '../lib/vehicleWorkspaceSelection';
import { VehicleListSkeleton, VehicleWorkspaceSkeleton } from '../components/TmSkeletonLayouts';
import { Skeleton } from '../components/ui/skeleton';
import { STATE_ORDER, statusOf, TYRE_STATES } from '../components/vehicleDiagram/diagramTheme';

const nowForInput = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const initialOperation = () => ({
  idempotencyKey: uuidv4(),
  tyreId: '', effectiveAt: nowForInput(), odometerKm: '', reason: '', condition: '', photo: null,
  destinationVehicleId: '', destinationPositionId: '', destinationOdometerKm: '',
});

const initialInlineTyreForm = (site = '') => ({
  site,
  tyre_id: '',
  serial_number: '',
  brand: '',
  model: '',
  size: '',
  tyre_type: '',
  initial_tread_depth_mm: '',
});

const STATUS_MEANINGS = {
  normal: 'Tyre is within its configured running limit.',
  warning: 'Approaching its running limit — plan the next service.',
  due: 'The retreading threshold has been reached.',
  overdue: 'Running beyond its configured retreading threshold.',
  under_maintenance: 'This tyre is currently under maintenance.',
  damaged: 'The tyre is marked damaged and should be inspected before further use.',
  failed: 'The tyre has failed and must not be returned to service.',
  data_unavailable: 'Running-limit data is incomplete and needs review.',
  empty: 'No tyre is fitted to this position.',
};

const InlineTyreForm = ({ form, onChange, onBack, siteName, operation, error }) => (
  <div className="space-y-5">
    <div className="flex items-start gap-3">
      <Button type="button" variant="ghost" size="sm" className="-ml-2 shrink-0" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" /> Back to {operation === 'replace' ? 'replacement' : 'assignment'}
      </Button>
    </div>
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-4">
      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-700">Add to inventory</p>
      <p className="mt-1 font-bold text-slate-950">Create a tyre and keep this {operation === 'replace' ? 'replacement' : 'assignment'} open</p>
      <p className="mt-1 text-sm leading-6 text-slate-600">
        The new tyre will be available at <strong className="font-semibold text-slate-800">{siteName || 'this site'}</strong> and selected automatically when you return.
      </p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField label="Manufacturer serial" htmlFor="inline-tyre-serial" required>
        <Input
          id="inline-tyre-serial"
          name="serial_number"
          value={form.serial_number}
          onChange={onChange}
          placeholder="e.g. SN-8839201"
          required
          autoFocus
        />
      </FormField>
      <FormField label="Brand" htmlFor="inline-tyre-brand" required>
        <Input
          id="inline-tyre-brand"
          name="brand"
          value={form.brand}
          onChange={onChange}
          placeholder="e.g. Michelin, Apollo, MRF"
          required
        />
      </FormField>
      <FormField label="Model" htmlFor="inline-tyre-model" required>
        <Input
          id="inline-tyre-model"
          name="model"
          value={form.model}
          onChange={onChange}
          placeholder="e.g. X Multi Energy"
          required
        />
      </FormField>
      <FormField label="Size" htmlFor="inline-tyre-size" required>
        <Input
          id="inline-tyre-size"
          name="size"
          value={form.size}
          onChange={onChange}
          placeholder="e.g. 295/80 R22.5"
          required
        />
      </FormField>
      <FormField label="Tyre type" htmlFor="inline-tyre-type" required>
        <SelectControl id="inline-tyre-type" name="tyre_type" value={form.tyre_type} onChange={onChange} required>
          <option value="">Select tyre type</option>
          <option value="Radial">Radial</option>
          <option value="Radial (Tubeless)">Radial (Tubeless)</option>
          <option value="Radial (Tube-type)">Radial (Tube-type)</option>
          <option value="Bias / Cross-ply">Bias / Cross-ply</option>
          <option value="Solid / Industrial">Solid / Industrial</option>
        </SelectControl>
      </FormField>
      <FormField label="Tyre ID" htmlFor="inline-tyre-id" optional hint="Leave blank to generate an ID automatically.">
        <Input
          id="inline-tyre-id"
          name="tyre_id"
          value={form.tyre_id}
          onChange={onChange}
          placeholder="Optional stock ID"
        />
      </FormField>
      <FormField label="Initial tread depth (mm)" htmlFor="inline-tyre-tread" optional>
        <Input
          id="inline-tyre-tread"
          name="initial_tread_depth_mm"
          type="number"
          min="0"
          step="0.1"
          value={form.initial_tread_depth_mm}
          onChange={onChange}
          placeholder="Optional"
        />
      </FormField>
    </div>
    <p className="text-xs leading-5 text-slate-500">
      Purchase details, photos, and invoices can be added later from Tyres &amp; Inventory.
    </p>
    {error && (
      <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )}
  </div>
);

InlineTyreForm.propTypes = {
  form: PropTypes.shape({
    tyre_id: PropTypes.string,
    serial_number: PropTypes.string,
    brand: PropTypes.string,
    model: PropTypes.string,
    size: PropTypes.string,
    tyre_type: PropTypes.string,
    initial_tread_depth_mm: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  siteName: PropTypes.string,
  operation: PropTypes.string.isRequired,
  error: PropTypes.string,
};

const TyreVehiclesPage = () => {
  const location = useLocation();
  const history = useHistory();
  const { getUser } = useAuth();
  const userRole = getUser()?.role;
  const isAdmin = userRole === USER_ROLES.admin;
  const canCreateVehicle = [USER_ROLES.admin, USER_ROLES.siteManager].includes(userRole);
  const [search, setSearch] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [operation, setOperation] = useState('');
  const [form, setForm] = useState(initialOperation);
  const [assignmentStep, setAssignmentStep] = useState('select');
  const [inlineTyreForm, setInlineTyreForm] = useState(() => initialInlineTyreForm());
  const [inlineTyreError, setInlineTyreError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [creatingInlineTyre, setCreatingInlineTyre] = useState(false);
  const [operationError, setOperationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [attachmentWarning, setAttachmentWarning] = useState('');
  const [tyreQuery, setTyreQuery] = useState('');
  const [debouncedTyreQuery, setDebouncedTyreQuery] = useState('');
  const [queryNotice, setQueryNotice] = useState('');
  const selectedTyrePanelRef = useRef(null);
  const createTyreMutation = useTyreMutation(tyreManagementApi.createTyre);
  const operationMutation = useTyreMutation(({ type, payload }) => tyreManagementApi[type](payload));
  const uploadAttachmentMutation = useTyreMutation(
    ({ tyreId, body }) => tyreManagementApi.uploadTyreAttachment(tyreId, body)
  );

  const vehiclesState = useAsyncData(tyreQueryKeys.vehicles(), () => tyreManagementApi.getVehicles());
  const vehicles = vehiclesState.data || [];
  const selectedVehicle = vehicles.find((vehicle) => String(vehicle.id) === String(selectedVehicleId));
  const layoutState = useAsyncData(
    tyreQueryKeys.vehicleLayout(selectedVehicleId),
    () => tyreManagementApi.getVehicleLayout(selectedVehicleId),
    Boolean(selectedVehicleId && selectedVehicle?.active_configuration_id)
  );
  const availableState = useAsyncData(
    tyreQueryKeys.tyres({ custody: 'available', site: selectedVehicle?.site, search: debouncedTyreQuery || undefined, page_size: 100 }),
    () => tyreManagementApi.getTyres({
      custody: 'available',
      site: selectedVehicle?.site,
      search: debouncedTyreQuery || undefined,
      page_size: 100,
    }),
    Boolean(selectedVehicle?.site)
  );
  const configState = useAsyncData(tyreQueryKeys.configuration(), tyreManagementApi.getConfiguration);
  const canManageMaster = configState.data?.can_manage_master === true;
  const availableTyres = unwrapList(availableState.data);
  const positions = layoutState.data?.positions || [];
  const selectedPosition = positions.find((position) => String(position.id) === String(selectedPositionId)) || null;
  const selectedTyreUuid = selectedPosition?.current_fitment?.tyre_uuid;
  const tyreDetailState = useAsyncData(
    tyreQueryKeys.tyre(selectedTyreUuid),
    () => tyreManagementApi.getTyre(selectedTyreUuid),
    Boolean(selectedTyreUuid)
  );
  const destinationLayoutState = useAsyncData(
    tyreQueryKeys.vehicleLayout(form.destinationVehicleId),
    () => tyreManagementApi.getVehicleLayout(form.destinationVehicleId),
    Boolean(operation === 'transfer' && form.destinationVehicleId)
  );
  const destinationPositions = (destinationLayoutState.data?.positions || []).filter((position) => !position.current_fitment);
  const requestedVehicleId = useMemo(
    () => new URLSearchParams(location.search).get('vehicle'),
    [location.search]
  );
  const requestedPositionId = useMemo(
    () => new URLSearchParams(location.search).get('position'),
    [location.search]
  );

  useEffect(() => {
    const nextVehicleId = resolveSelectedVehicleId({
      vehicles,
      requestedVehicleId,
      selectedVehicleId,
    });
    if (String(nextVehicleId) === String(selectedVehicleId)) return;
    setSelectedVehicleId(nextVehicleId);
    if (nextVehicleId && requestedVehicleId) setQueryNotice('');
  }, [requestedVehicleId, selectedVehicleId, vehicles]);

  useEffect(() => {
    setSelectedPositionId('');
    setQueryNotice('');
  }, [selectedVehicleId]);

  useEffect(() => {
    if (!requestedPositionId || layoutState.loading || !layoutState.data) return;
    const requested = positions.find((position) => String(position.id) === String(requestedPositionId));
    if (requested) {
      if (String(selectedPositionId) !== String(requested.id)) setSelectedPositionId(String(requested.id));
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete('position');
    history.replace({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' });
    setQueryNotice('That tyre position is not part of this vehicle layout. The invalid position was cleared.');
  }, [history, layoutState.data, layoutState.loading, location.pathname, location.search, positions, requestedPositionId, selectedPositionId]);

  useEffect(() => {
    if (selectedPositionId) selectedTyrePanelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedPositionId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedTyreQuery(tyreQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [tyreQuery]);

  const filteredVehicles = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? vehicles.filter((vehicle) => `${vehicle.registration_num} ${vehicle.model || ''} ${vehicle.site_name || ''}`.toLowerCase().includes(query))
      : vehicles;
  }, [search, vehicles]);

  const replaceQuery = (vehicleId, positionId) => {
    const params = new URLSearchParams(location.search);
    if (vehicleId) params.set('vehicle', vehicleId);
    else params.delete('vehicle');
    if (positionId) params.set('position', positionId);
    else params.delete('position');
    history.replace({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' });
  };

  const selectVehicle = (vehicleId) => {
    setSelectedVehicleId(String(vehicleId));
    setQueryNotice('');
    replaceQuery(String(vehicleId), '');
  };

  const selectPosition = (position) => {
    setSelectedPositionId(String(position.id));
    setSuccessMessage('');
    replaceQuery(selectedVehicleId, String(position.id));
  };

  const clearPosition = () => {
    setSelectedPositionId('');
    replaceQuery(selectedVehicleId, '');
  };

  const openOperation = (type) => {
    setForm(initialOperation());
    setAssignmentStep('select');
    setInlineTyreForm(initialInlineTyreForm(selectedVehicle?.site));
    setInlineTyreError('');
    setOperationError('');
    setSuccessMessage('');
    setAttachmentWarning('');
    setTyreQuery('');
    setOperation(type);
  };

  const closeOperation = () => {
    setOperation('');
    setAssignmentStep('select');
    setInlineTyreError('');
  };

  const handleInlineTyreForm = (event) => {
    setInlineTyreForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const createInlineTyre = async (event) => {
    event.preventDefault();
    setCreatingInlineTyre(true);
    setInlineTyreError('');
    try {
      const payload = { ...inlineTyreForm };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') delete payload[key];
      });
      const created = await createTyreMutation.mutateAsync(payload);
      if (!created?.id) throw new Error('The tyre was created, but it could not be selected for this operation.');
      const createdSearch = created.tyre_id || created.serial_number || inlineTyreForm.tyre_id || inlineTyreForm.serial_number;
      setTyreQuery(createdSearch);
      setDebouncedTyreQuery(createdSearch);
      setForm((current) => ({ ...current, tyreId: created.id }));
      setAssignmentStep('select');
    } catch (requestError) {
      setInlineTyreError(requestError.message);
    } finally {
      setCreatingInlineTyre(false);
    }
  };

  const submitOperation = async (event) => {
    event.preventDefault();
    if (operation === 'transfer') {
      if (!form.destinationVehicleId) {
        setOperationError('Select a destination vehicle before transferring this tyre.');
        return;
      }
      if (destinationLayoutState.loading || destinationLayoutState.error || !destinationPositions.length) {
        setOperationError('A loaded destination with an empty tyre position is required before transfer.');
        return;
      }
      if (!form.destinationPositionId) {
        setOperationError('Select an empty destination position before transferring this tyre.');
        return;
      }
    }
    setSubmitting(true);
    setOperationError('');
    try {
      const common = {
        position_id: selectedPosition.id,
        effective_at: new Date(form.effectiveAt).toISOString(),
        idempotency_key: form.idempotencyKey,
      };
      if (form.odometerKm !== '') common.odometer_km = form.odometerKm;
      let result;
      if (operation === 'assign') {
        result = await operationMutation.mutateAsync({ type: 'assignTyre', payload: { ...common, tyre_id: form.tyreId } });
      } else if (operation === 'remove') {
        result = await operationMutation.mutateAsync({ type: 'removeTyre', payload: {
          ...common, reason: form.reason, condition: form.condition,
        } });
      } else if (operation === 'replace') {
        result = await operationMutation.mutateAsync({ type: 'replaceTyre', payload: {
          ...common, new_tyre_id: form.tyreId, reason: form.reason, old_condition: form.condition,
        } });
      } else {
        result = await operationMutation.mutateAsync({ type: 'transferTyre', payload: {
          source_position_id: selectedPosition.id,
          destination_position_id: form.destinationPositionId,
          effective_at: common.effective_at,
          source_odometer_km: form.odometerKm || undefined,
          destination_odometer_km: form.destinationOdometerKm || undefined,
          reason: form.reason,
          idempotency_key: common.idempotency_key,
        } });
      }
      let photoWarning = '';
      if (form.photo && operation !== 'assign') {
        try {
          const body = new FormData();
          body.append('file', form.photo);
          body.append('category', operation === 'remove' ? 'removal' : 'replacement');
          body.append('lifecycle_event', result.id);
          await uploadAttachmentMutation.mutateAsync({
            tyreId: selectedPosition.current_fitment.tyre_uuid,
            body,
          });
        } catch (uploadError) {
          photoWarning = `Operation saved, but the photo upload failed: ${uploadError.message}`;
        }
      }
      const completedOperation = operation;
      setOperation('');
      setSuccessMessage(`${completedOperation[0].toUpperCase()}${completedOperation.slice(1)} completed.`);
      setAttachmentWarning(photoWarning);
    } catch (requestError) {
      setOperationError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const occupancy = layoutState.data?.occupancy;
  const operationNeedsTyre = operation === 'assign' || operation === 'replace';
  const tyreSelectionBlocked = operationNeedsTyre
    && (availableState.loading || Boolean(availableState.error) || availableTyres.length === 0 || !form.tyreId);
  const transferBlocked = operation === 'transfer' && (
    !form.destinationVehicleId
    || destinationLayoutState.loading
    || Boolean(destinationLayoutState.error)
    || destinationPositions.length === 0
    || !form.destinationPositionId
  );
  const classificationBlocked = (operation !== 'assign' && !form.reason.trim())
    || ((operation === 'remove' || operation === 'replace') && !form.condition);
  const operationBlocked = tyreSelectionBlocked || transferBlocked || classificationBlocked;
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(STATE_ORDER.map((key) => [key, 0]));
    positions.forEach((position) => { counts[statusOf(position)] += 1; });
    return counts;
  }, [positions]);
  const filteredAvailableTyres = useMemo(() => {
    const query = tyreQuery.trim().toLowerCase();
    return availableTyres.filter((tyre) => !query || `${tyre.tyre_id} ${tyre.brand || ''} ${tyre.model || ''} ${tyre.size || ''}`.toLowerCase().includes(query)).slice(0, 40);
  }, [availableTyres, tyreQuery]);
  const showNextStatus = (state) => {
    const matching = positions.filter((position) => statusOf(position) === state);
    if (!matching.length) return;
    const current = matching.findIndex((position) => String(position.id) === String(selectedPositionId));
    selectPosition(matching[(current + 1) % matching.length]);
  };
  const showNextIssue = () => {
    const flagged = positions.filter((position) => statusOf(position) !== 'normal');
    if (!flagged.length) return;
    const current = flagged.findIndex((position) => String(position.id) === String(selectedPositionId));
    selectPosition(flagged[(current + 1) % flagged.length]);
  };
  const tyreDetail = tyreDetailState.data || selectedPosition?.current_fitment || {};
  const currentFitment = selectedPosition?.current_fitment || {};
  const runningKm = selectedPosition?.current_fitment?.running_km;
  const thresholdKm = tyreDetail.threshold_km || tyreDetail.retreading_threshold_km;
  const installedOn = currentFitment.installed_on
    || currentFitment.installed_at
    || tyreDetail.installed_on
    || tyreDetail.installed_at;
  const installationOdometer = currentFitment.installation_odometer_km
    ?? currentFitment.install_odometer_km
    ?? tyreDetail.installation_odometer_km
    ?? tyreDetail.install_odometer_km;
  const wearPercent = thresholdKm > 0 ? Math.min(100, (Number(runningKm || 0) / Number(thresholdKm)) * 100) : null;
  const currentOdometer = layoutState.data?.odometer_km
    ?? selectedVehicle?.odometer_km
    ?? selectedVehicle?.current_odometer_km
    ?? selectedVehicle?.latest_odometer_km
    ?? selectedVehicle?.last_odometer_km;
  const layoutName = layoutState.data?.layout_template?.name || 'Tyre layout';
  const vehicleClass = layoutState.data?.layout_template?.vehicle_class?.name
    || selectedVehicle?.category
    || selectedVehicle?.model
    || 'Vehicle';
  const attentionStates = STATE_ORDER.filter((key) => key !== 'normal' && statusCounts[key]);
  const visibleStatusOrder = STATE_ORDER.filter((key) => key !== 'data_unavailable' || statusCounts[key]);
  const readinessColor = (vehicle) => {
    const value = String(vehicle.tyre_readiness || '').toLowerCase();
    if (value === 'complete' || value === 'configured' || value === 'ready') return TYRE_STATES.normal.color;
    if (value.includes('setup') || value.includes('unconfigured')) return TYRE_STATES.overdue.color;
    return value ? TYRE_STATES.warning.color : '#CBD5E1';
  };
  const vehicleWheelCount = (vehicle) => vehicle.wheel_count
    ?? vehicle.tyre_position_count
    ?? vehicle.position_count
    ?? vehicle.mandatory_position_count;
  const destinationVehicles = vehicles.filter((vehicle) => vehicle.active_configuration_id);
  const selectedVehicleRecordPath = selectedVehicle
    ? ROUTE.vehicleDetails.replace(':vehicleId', selectedVehicle.id)
    : '';
  const setupPath = selectedVehicle
    ? `${ROUTE.tyreV2Setup}?vehicle=${encodeURIComponent(selectedVehicle.id)}`
    : ROUTE.tyreV2Setup;
  const requestedVehicleIsKnown = vehicles.some((vehicle) => String(vehicle.id) === String(requestedVehicleId));
  const invalidRequestedVehicle = !vehiclesState.loading
    && !vehiclesState.error
    && requestedVehicleId !== null
    && !requestedVehicleIsKnown;
  const confirmLabel = {
    assign: 'Confirm assignment',
    remove: 'Confirm removal',
    replace: 'Confirm replacement',
    transfer: 'Confirm transfer',
  }[operation] || 'Confirm operation';

  return (
    <ClassNames>
      {({ css }) => (
        <div className="tm-focus-page">
      <nav className="tm-focus-context-nav" aria-label="Tyre workspace navigation">
        <strong>Tyre workspace</strong>
        <div>
          <Link to={ROUTE.tyreV2Overview}><CircleGauge />Tyre overview</Link>
          <Link to={ROUTE.vehicles}><Truck />Vehicle List</Link>
          <Link to={ROUTE.tyreV2Inventory}><Warehouse />Inventory</Link>
          {isAdmin && <Link to={setupPath}><Settings2 />Setup</Link>}
        </div>
        <div className="tm-focus-context-actions">
          {selectedVehicle && <Link to={selectedVehicleRecordPath}><ExternalLink />{selectedVehicle.registration_num} record</Link>}
          {canCreateVehicle && <Link to={`${ROUTE.vehicleCreate}?from=tyres`}><PlusCircle />New vehicle</Link>}
        </div>
      </nav>
      <div className="tm-focus-app">
        <aside className="tm-focus-fleet" aria-label="Vehicles">
          <div className="tm-focus-fleet-head">
            <div>
              <h1>Vehicles</h1>
              <p>{vehiclesState.loading ? 'Loading fleet…' : `${formatNumber(vehicles.length)} registrations`}</p>
            </div>
            <Button asChild variant="ghost" size="icon"><Link to={ROUTE.tyreV2Overview} aria-label="Back to tyre management"><ArrowLeft /></Link></Button>
          </div>
          <div className="tm-focus-fleet-search">
            <Search />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vehicles" disabled={vehiclesState.loading} />
          </div>
          <nav className="tm-focus-fleet-list">
            {vehiclesState.loading && <VehicleListSkeleton />}
            {!vehiclesState.loading && filteredVehicles.map((vehicle) => {
              const active = String(vehicle.id) === String(selectedVehicleId);
              return (
                <button key={vehicle.id} type="button" data-selected={active || undefined} onClick={() => selectVehicle(vehicle.id)}>
                  <span className="tm-focus-fleet-row-main">
                   <span className="tm-focus-fleet-registration"><i className={css({ backgroundColor: readinessColor(vehicle) })} />{vehicle.registration_num}</span>
                    <small>{[vehicle.model || vehicle.category, vehicle.site_name].filter(Boolean).join(' · ') || 'Vehicle details not recorded'}</small>
                  </span>
                  <span className="tm-focus-fleet-row-meta">
                    {vehicleWheelCount(vehicle) ? `${vehicleWheelCount(vehicle)} wheels` : String(vehicle.tyre_readiness || 'View')}
                  </span>
                </button>
              );
            })}
            {vehiclesState.error && <EmptyState title="Vehicles unavailable" description={vehiclesState.error} action={<Button type="button" onClick={vehiclesState.reload}>Retry</Button>} />}
            {!vehiclesState.loading && !vehiclesState.error && Boolean(vehicles.length) && !filteredVehicles.length && <EmptyState title="No vehicles match" description="Try another registration, model, or site." />}
            {!vehiclesState.loading && !vehiclesState.error && !vehicles.length && (
              <EmptyState
                title="No vehicles have tyre tracking on"
                description="Turn on tyre tracking for a vehicle before choosing its layout and fitting tyres."
                action={<Button asChild variant="outline"><Link to={ROUTE.vehicles}>Open Vehicle List</Link></Button>}
              />
            )}
          </nav>
          {canCreateVehicle && <Button asChild variant="ghost" className="tm-focus-new-vehicle"><Link to={`${ROUTE.vehicleCreate}?from=tyres`}><PlusCircle /> New vehicle</Link></Button>}
        </aside>

        <main className="tm-focus-main">
          <header className="tm-focus-vehicle-header">
            <div className="tm-focus-vehicle-facts">
              <strong>{selectedVehicle?.registration_num || 'Select a vehicle'}</strong>
              {selectedVehicle && <span>{vehicleClass} · {layoutName} · {positions.length || '—'} wheels</span>}
              {selectedVehicle?.site_name && <span className="tm-focus-site"><MapPin />{selectedVehicle.site_name}</span>}
              {selectedVehicle && (
                <span className="tm-focus-odometer">
                  <small>Odometer</small>
                  <b>{currentOdometer != null ? `${formatNumber(currentOdometer)} km` : 'Not recorded'}</b>
                </span>
              )}
            </div>
            <div className="tm-focus-header-actions">
              {attentionStates.map((key) => (
                 <button key={key} type="button" onClick={() => showNextStatus(key)} className={css({ '--status-color': TYRE_STATES[key].color })}>
                  <i />{statusCounts[key]} {TYRE_STATES[key].label.toLowerCase()}
                </button>
              ))}
              {!layoutState.loading && positions.length > 0 && !attentionStates.length && <span className="tm-focus-all-normal"><i />All {positions.length} tyres normal</span>}
              {occupancy && <span className="tm-focus-occupancy"><Disc3 />{occupancy.fitted}/{occupancy.mandatory} fitted</span>}
              <Button variant="ghost" size="icon" onClick={() => Promise.all([vehiclesState.reload(), layoutState.reload()])} aria-label="Refresh workspace"><RefreshCw /></Button>
            </div>
          </header>

          <div className="tm-focus-workarea">
            {vehiclesState.loading && <div className="tm-focus-workspace-state"><VehicleWorkspaceSkeleton /></div>}
            {!vehiclesState.loading && vehiclesState.error && <div className="tm-focus-workspace-state"><EmptyState title="Tyre vehicles unavailable" description={vehiclesState.error} action={<Button type="button" onClick={vehiclesState.reload}>Retry</Button>} /></div>}
            {!vehiclesState.loading && !vehiclesState.error && invalidRequestedVehicle && <div className="tm-focus-workspace-state"><EmptyState title="Vehicle not found" description={`The requested vehicle (${requestedVehicleId || 'blank ID'}) is not available in this tyre workspace.`} action={<Button type="button" onClick={() => { setSelectedVehicleId(''); replaceQuery('', ''); }}>Choose from vehicle list</Button>} /></div>}
            {!vehiclesState.loading && !vehiclesState.error && Boolean(vehicles.length) && !selectedVehicle && !invalidRequestedVehicle && <div className="tm-focus-workspace-state"><EmptyState title="Choose a vehicle" description="Select a vehicle from the fleet to manage its tyres." /></div>}
            {!vehiclesState.loading && selectedVehicle && !selectedVehicle.active_configuration_id && (
              <div className="tm-focus-workspace-state">
                <EmptyState
                  title="Layout not configured"
                  description={isAdmin ? 'Assign a vehicle layout before tyres can be fitted.' : 'A tyre layout must be assigned by an administrator before tyres can be fitted.'}
                  action={isAdmin ? (
                    <Button asChild><Link to={setupPath}>Configure this vehicle</Link></Button>
                  ) : (
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button asChild variant="outline"><Link to={selectedVehicleRecordPath}>Open vehicle record</Link></Button>
                      <Button type="button" variant="ghost" onClick={() => { setSelectedVehicleId(''); replaceQuery('', ''); }}>Choose another vehicle</Button>
                    </div>
                  )}
                />
              </div>
            )}
            {!vehiclesState.loading && selectedVehicle?.active_configuration_id && layoutState.error && <div className="tm-focus-workspace-state"><EmptyState title="Layout unavailable" description={layoutState.error} /></div>}
            {!vehiclesState.loading && selectedVehicle?.active_configuration_id && layoutState.loading && <div className="tm-focus-workspace-state"><VehicleWorkspaceSkeleton /></div>}

            {!vehiclesState.loading && selectedVehicle?.active_configuration_id && !layoutState.loading && layoutState.data && (
              <div className="tm-focus-vehicle-workspace">
                <section className="tm-focus-vehicle-stage" aria-label="Interactive vehicle tyre view">
                  {queryNotice && <div className="tm-focus-query-notice" role="status"><AlertCircle />{queryNotice}<button type="button" onClick={() => setQueryNotice('')}>Dismiss</button></div>}
                  <ClaudeVehicleLayout vehicle={selectedVehicle} configuration={layoutState.data} selectedPositionId={selectedPositionId} onSelectPosition={selectPosition} />
                </section>

                <aside className="tm-focus-tyre-rail" aria-label="Vehicle tyre details">
                  <div className="tm-focus-rail-head">
                    <div><h2>Tyre positions</h2><p>{positions.length} on this vehicle</p></div>
                    {selectedPosition && <button type="button" onClick={clearPosition}>Clear</button>}
                  </div>

                  <div className="tm-focus-position-map">
                    <ClaudePositionMap
                      positions={positions}
                      selectedPositionId={selectedPositionId}
                      onSelectPosition={selectPosition}
                      vehicleNumber={selectedVehicle.registration_num}
                    />
                  </div>

                  <section className="tm-focus-status-legend" aria-label="Tyre status">
                    <h3>Tyre status</h3>
                    {visibleStatusOrder.map((key) => (
                      <button key={key} type="button" data-status={key} disabled={!statusCounts[key]} onClick={() => showNextStatus(key)}>
                         <i className={css({ '--tyre-color': TYRE_STATES[key].color, '--tyre-edge': TYRE_STATES[key].edge })}>{TYRE_STATES[key].mark}</i><span>{TYRE_STATES[key].label}</span><strong>{statusCounts[key]}</strong>
                      </button>
                    ))}
                  </section>

                  <section ref={selectedTyrePanelRef} className="tm-focus-selected-tyre" aria-live="polite">
                    {!selectedPosition ? (
                      <div className="tm-focus-selection-empty"><h3>Selected tyre</h3><p>Pick a position on the plan above, or a wheel on the vehicle, to see the tyre fitted to it.</p></div>
                    ) : (
                      <>
                        <div className="tm-focus-selected-head">
                          <div><p>Selected tyre</p><h3>{selectedPosition.label}</h3><small>{selectedPosition.code} · Axle {selectedPosition.axle_index}</small></div>
                         <i className={css({ backgroundColor: TYRE_STATES[statusOf(selectedPosition)].color })} />
                        </div>
                        {successMessage && <div className="tm-focus-alert is-success" role="status"><CheckCircle2 />{successMessage}<button type="button" onClick={showNextIssue}>Next issue</button></div>}
                        {attachmentWarning && <div className="tm-focus-alert is-warning" role="alert"><AlertCircle />{attachmentWarning}</div>}
                        {selectedPosition.current_fitment ? (
                          <>
                            <div className="tm-focus-tyre-identity"><div><span>Tyre ID</span><strong>{selectedPosition.current_fitment.tyre_id}</strong></div><StateBadge value={selectedPosition.current_fitment.alert_state} /></div>
                            <p className="tm-focus-status-meaning">{TYRE_STATES[statusOf(selectedPosition)].label}<span>{STATUS_MEANINGS[statusOf(selectedPosition)]}</span></p>
                             {wearPercent != null && <div className="tm-focus-wear"><div><span>Distance against threshold</span><strong>{Math.round(wearPercent)}%</strong></div><div><i className={css({ width: `${wearPercent}%`, backgroundColor: TYRE_STATES[statusOf(selectedPosition)].color })} /><b /></div><p>{formatNumber(runningKm, { maximumFractionDigits: 0 })} of {formatNumber(thresholdKm, { maximumFractionDigits: 0 })} km</p></div>}
                            {tyreDetailState.loading ? <div className="space-y-2 py-3"><Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} /></div> : (
                              <dl className="tm-focus-detail-list">
                                <div><dt>Manufacturer serial</dt><dd className="tm-mono">{tyreDetail.serial_number || tyreDetail.serial || 'Not recorded'}</dd></div>
                                <div><dt>Tyre</dt><dd>{[tyreDetail.brand, tyreDetail.model, tyreDetail.size].filter(Boolean).join(' · ') || 'Not recorded'}</dd></div>
                                <div><dt>Tyre type</dt><dd>{tyreDetail.tyre_type || 'Not recorded'}</dd></div>
                                <div><dt>Installed on</dt><dd>{formatDate(installedOn)}</dd></div>
                                <div><dt>Install km</dt><dd>{installationOdometer != null ? formatNumber(installationOdometer) : 'Not recorded'}</dd></div>
                                <div><dt>Current km</dt><dd>{currentOdometer != null ? formatNumber(currentOdometer) : 'Not recorded'}</dd></div>
                                <div><dt>Running km</dt><dd>{runningKm != null ? formatNumber(runningKm, { maximumFractionDigits: 0 }) : 'Not recorded'}</dd></div>
                                <div><dt>Threshold</dt><dd>{thresholdKm ? formatNumber(thresholdKm) : 'Not recorded'}</dd></div>
                                <div><dt>Condition</dt><dd><StateBadge value={selectedPosition.current_fitment.condition} /></dd></div>
                              </dl>
                            )}
                            <div className="tm-focus-tyre-actions"><Button variant="outline" onClick={() => openOperation('remove')}><Wrench /> Remove</Button><Button onClick={() => openOperation('replace')}><RefreshCw /> Replace</Button><Button variant="secondary" onClick={() => openOperation('transfer')}><ArrowRightLeft /> Transfer</Button></div>
                          </>
                        ) : (
                          <div className="tm-focus-empty-position"><PlusCircle /><h3>Position is empty</h3><p>Choose a serviceable tyre from {selectedVehicle.site_name}.</p><Button onClick={() => openOperation('assign')}>Assign tyre</Button></div>
                        )}
                      </>
                    )}
                  </section>
                </aside>
              </div>
            )}
          </div>
        </main>
      </div>

      <Dialog open={Boolean(operation)} onOpenChange={(open) => !open && closeOperation()}>
        <DialogContent side className="tm-focus-operation-sheet !left-auto !right-0 !top-0 !h-[100dvh] !max-h-none !w-[min(520px,100%)] !max-w-none !translate-x-0 !translate-y-0 !rounded-none">
          <DialogHeader className="shrink-0">
            <DialogTitle>{operation === 'assign' ? 'Assign tyre' : operation === 'remove' ? 'Remove tyre' : operation === 'replace' ? 'Replace tyre' : 'Transfer tyre'}</DialogTitle>
            <DialogDescription>{selectedPosition?.label} on {selectedVehicle?.registration_num}. The change will be recorded in history.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={assignmentStep === 'create' ? createInlineTyre : submitOperation}
            className="flex min-h-0 min-w-0 flex-1 flex-col"
          >
            <DialogBody className="min-w-0 space-y-5">
              {assignmentStep === 'create' ? (
                <InlineTyreForm
                  form={inlineTyreForm}
                  onChange={handleInlineTyreForm}
                  onBack={() => setAssignmentStep('select')}
                  siteName={selectedVehicle?.site_name}
                  operation={operation}
                  error={inlineTyreError}
                />
              ) : (
                <>
                  {operationNeedsTyre && (
                    availableState.loading ? (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" role="status">
                        Checking available tyres…
                      </div>
                    ) : availableState.error ? (
                      <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                        <div className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{availableState.error}</span></div>
                        <Button type="button" variant="outline" size="sm" onClick={() => availableState.reload()}>Try again</Button>
                      </div>
                    ) : availableTyres.length ? (
                      <div className="space-y-2">
                        <FormField label={operation === 'assign' ? 'Available tyre' : 'Replacement tyre'} htmlFor="operation-tyre-search" required>
                          <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input id="operation-tyre-search" className="pl-9" value={tyreQuery} onChange={(event) => setTyreQuery(event.target.value)} placeholder="Search ID, brand, model or size" /></div>
                        </FormField>
                        <div className="tm-focus-tyre-picker" role="listbox" aria-label="Available tyres">
                          {filteredAvailableTyres.map((tyre) => <button key={tyre.id} type="button" role="option" aria-selected={String(form.tyreId) === String(tyre.id)} data-selected={String(form.tyreId) === String(tyre.id) || undefined} onClick={() => setForm({ ...form, tyreId: tyre.id })}><span><strong>{tyre.tyre_id}</strong><small>{tyre.brand} {tyre.model} · {tyre.condition || 'Condition not recorded'}</small></span><span className="tm-mono">{tyre.size}{(tyre.current_tread_depth_mm ?? tyre.tread_depth_mm) !== undefined ? ` · ${tyre.current_tread_depth_mm ?? tyre.tread_depth_mm} mm` : ''}</span></button>)}
                          {!filteredAvailableTyres.length && (
                            <div className="tm-focus-picker-empty">
                              <p>No tyres match this search.</p>
                              {canManageMaster ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setInlineTyreError('');
                                    setInlineTyreForm(initialInlineTyreForm(selectedVehicle?.site));
                                    setAssignmentStep('create');
                                  }}
                                >
                                  <PlusCircle />Add a new tyre instead
                                </Button>
                              ) : (
                                <small>Clear the search or ask an inventory manager to add a tyre.</small>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/70 p-5">
                        <div className="flex items-start gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-white text-amber-700 shadow-sm"><PlusCircle className="h-5 w-5" /></span>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-950">No {operation === 'replace' ? 'replacement ' : ''}tyres available</p>
                            <p className="mt-1 text-sm leading-6 text-slate-600">Add a tyre at {selectedVehicle?.site_name || 'this site'} here and continue without leaving this {operation === 'replace' ? 'replacement' : 'assignment'}.</p>
                            {canManageMaster ? (
                              <Button
                                type="button"
                                className="mt-4"
                                onClick={() => {
                                  setInlineTyreError('');
                                  setInlineTyreForm(initialInlineTyreForm(selectedVehicle?.site));
                                  setAssignmentStep('create');
                                }}
                              >
                                <PlusCircle className="h-4 w-4" /> Add new tyre
                              </Button>
                            ) : !configState.loading && (
                              <p className="mt-3 text-xs font-medium text-amber-800">Ask an inventory manager to add a tyre before continuing.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                  {!tyreSelectionBlocked && (
                    <>
                      {operation === 'transfer' && (
                        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                          <FormField className="min-w-0" label="Destination vehicle" htmlFor="destination-vehicle" required>
                            <SelectControl id="destination-vehicle" value={form.destinationVehicleId} onChange={(event) => { setOperationError(''); setForm({ ...form, destinationVehicleId: event.target.value, destinationPositionId: '' }); }} required searchable searchPlaceholder="Search destination vehicles…">
                              <option value="">Select destination vehicle</option>
                              {destinationVehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.registration_num} · {vehicle.site_name}</option>)}
                            </SelectControl>
                          </FormField>
                          <FormField className="min-w-0" label="Empty destination position" htmlFor="destination-position" required>
                            <SelectControl id="destination-position" value={form.destinationPositionId} onChange={(event) => { setOperationError(''); setForm({ ...form, destinationPositionId: event.target.value }); }} required disabled={!form.destinationVehicleId || destinationLayoutState.loading || Boolean(destinationLayoutState.error) || !destinationPositions.length} searchable searchPlaceholder="Search empty positions…">
                              <option value="">{destinationLayoutState.loading ? 'Loading positions…' : 'Select position'}</option>
                              {destinationPositions.map((position) => <option key={position.id} value={position.id}>{position.label} · {position.code}</option>)}
                            </SelectControl>
                          </FormField>
                          {form.destinationVehicleId && destinationLayoutState.loading && <div className="tm-focus-transfer-guidance sm:col-span-2" role="status"><RefreshCw className="animate-spin" />Loading destination layout and empty positions…</div>}
                          {form.destinationVehicleId && !destinationLayoutState.loading && destinationLayoutState.error && <div className="tm-focus-transfer-guidance is-error sm:col-span-2" role="alert"><AlertCircle />Could not load destination positions: {destinationLayoutState.error}<Button type="button" variant="outline" size="sm" onClick={destinationLayoutState.reload}>Try again</Button></div>}
                          {form.destinationVehicleId && !destinationLayoutState.loading && !destinationLayoutState.error && !destinationPositions.length && <div className="tm-focus-transfer-guidance is-warning sm:col-span-2" role="alert"><AlertCircle />This vehicle has no empty tyre positions. Choose another destination vehicle or remove a tyre there first.</div>}
                          {!form.destinationVehicleId && <p className="tm-focus-transfer-help sm:col-span-2">Choose a destination vehicle to load its empty tyre positions. Transfer remains blocked until an empty position is selected.</p>}
                          {!form.destinationVehicleId && !destinationVehicles.length && <div className="tm-focus-transfer-guidance is-warning sm:col-span-2" role="alert"><AlertCircle />No configured vehicle is available as a transfer destination.</div>}
                          <FormField className="min-w-0" label="Destination odometer (optional)" htmlFor="destination-odometer"><Input id="destination-odometer" type="number" min="0" step="0.01" value={form.destinationOdometerKm} onChange={(event) => setForm({ ...form, destinationOdometerKm: event.target.value })} placeholder="Use accepted reading" /></FormField>
                        </div>
                      )}
                      <div className="grid min-w-0 gap-4 sm:grid-cols-2"><FormField className="min-w-0" label="Effective date and time" htmlFor="effective-at" required><Input id="effective-at" type="datetime-local" value={form.effectiveAt} onChange={(event) => setForm({ ...form, effectiveAt: event.target.value })} required className="min-w-0" /></FormField><FormField className="min-w-0" label="Verified odometer (optional)" htmlFor="odometer" hint="Uses the latest accepted reading unless you enter a correction."><Input id="odometer" type="number" min="0" step="0.01" value={form.odometerKm} onChange={(event) => setForm({ ...form, odometerKm: event.target.value })} placeholder="Use accepted reading" /></FormField></div>
                      {operation !== 'assign' && <FormField label="Reason" htmlFor="operation-reason" required><Textarea id="operation-reason" value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} placeholder={operation === 'transfer' ? 'Why is this tyre moving?' : 'Why is this tyre being removed?'} required /></FormField>}
                      {(operation === 'remove' || operation === 'replace') && <><FormField label="Condition after removal" htmlFor="condition" required><SelectControl id="condition" value={form.condition} onChange={(event) => setForm({ ...form, condition: event.target.value })} required><option value="">Select condition</option><option value="serviceable">Serviceable</option><option value="worn">Worn</option><option value="damaged">Damaged</option><option value="failed">Failed</option></SelectControl></FormField><FormField label="Evidence photo (optional)" htmlFor="operation-photo" hint="JPEG, PNG, or WebP up to 10 MB."><Input id="operation-photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setForm({ ...form, photo: event.target.files[0] || null })} /></FormField></>}
                      {operationError && <div className="flex gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{operationError}</div>}
                    </>
                  )}
                </>
              )}
            </DialogBody>
            <DialogFooter className="shrink-0">
              {assignmentStep === 'create' ? (
                <>
                  <Button type="button" variant="outline" onClick={() => setAssignmentStep('select')} disabled={creatingInlineTyre}>Back</Button>
                  <Button type="submit" disabled={creatingInlineTyre}>{creatingInlineTyre ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{creatingInlineTyre ? 'Adding tyre…' : 'Add tyre & continue'}</Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={closeOperation}>Cancel</Button>
                  <Button type="submit" disabled={submitting || operationBlocked}>{submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{submitting ? 'Saving…' : confirmLabel}</Button>
                </>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
        </div>
      )}
    </ClassNames>
  );
};

export default TyreVehiclesPage;
